// WishlistSystem class for handling the wishlist button functionality
class WishlistSystem {
  constructor() {
    this.buttons = document.querySelectorAll('.wishlist-heart-button');
    this.wishlist = new Set(this.getWishlist());
    this.wishlistCount = document.querySelector('.wishlist-count');
    this.wishlistCountNumber = this.wishlistCount?.querySelector('[aria-hidden="true"]');
    this.init();
    this.updateWishlistCount(); // Update count on initialization
  }

  init() {
    this.buttons.forEach(button => {
      const productHandle = button.dataset.productHandle;
      if (this.wishlist.has(productHandle)) {
        button.classList.add('active');
      }
      button.addEventListener('click', (e) => this.toggleWishlist(e));
    });
    
    // Re-initialize when new content is loaded (for AJAX/dynamic content)
    this.observeForNewButtons();
  }
  
  observeForNewButtons() {
    // Watch for dynamically added content
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const newButtons = node.querySelectorAll ? node.querySelectorAll('.wishlist-heart-button') : [];
            newButtons.forEach(button => {
              if (!button.dataset.initialized) {
                const productHandle = button.dataset.productHandle;
                if (this.wishlist.has(productHandle)) {
                  button.classList.add('active');
                }
                button.addEventListener('click', (e) => this.toggleWishlist(e));
                button.dataset.initialized = 'true';
              }
            });
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  updateWishlistCount() {
    if (this.wishlistCount && this.wishlistCountNumber) {
      const count = this.wishlist.size;
      this.wishlistCountNumber.textContent = count;
      this.wishlistCount.classList.toggle('opacity-0', count === 0);
    }
  }

  toggleWishlist(event) {
    event.preventDefault();
    event.stopPropagation(); // Prevent navigation when clicking on product cards
    const button = event.currentTarget;
    const productHandle = button.dataset.productHandle;
    
    if (this.wishlist.has(productHandle)) {
      this.wishlist.delete(productHandle);
      button.classList.remove('active');
      this.showNotification(false);
    } else {
      this.wishlist.add(productHandle);
      button.classList.add('active');
      this.showNotification(true);
    }
    
    // Update all wishlist buttons for this product
    this.updateAllButtonsForProduct(productHandle);
    
    this.saveWishlist();
    this.updateWishlistCount(); // Update count after toggle
  }
  
  updateAllButtonsForProduct(productHandle) {
    const allButtons = document.querySelectorAll(`[data-product-handle="${productHandle}"]`);
    const isInWishlist = this.wishlist.has(productHandle);
    
    allButtons.forEach(button => {
      if (isInWishlist) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  }

  getWishlist() {
    const wishlist = localStorage.getItem('wishlist');
    return wishlist ? JSON.parse(wishlist) : [];
  }

  saveWishlist() {
    localStorage.setItem('wishlist', JSON.stringify([...this.wishlist]));
  }

  // New method to sync the internal Set with localStorage
  syncWithLocalStorage() {
    const localStorageWishlist = this.getWishlist();
    this.wishlist = new Set(localStorageWishlist);
    this.updateWishlistCount();
  }

  // New method to remove product from wishlist (to be called externally)
  removeFromWishlist(productHandle) {
    this.wishlist.delete(productHandle);
    this.saveWishlist();
    this.updateWishlistCount();
    this.updateAllButtonsForProduct(productHandle);
  }

  showNotification(isAdded) {
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = isAdded ? 'Added to wishlist!' : 'Removed from wishlist';
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 2000);
  }

  // New method to generate shareable URL
  generateShareableUrl() {
    // Sync with localStorage before generating URL to ensure we have the latest data
    this.syncWithLocalStorage();
    
    const wishlistArray = [...this.wishlist];
    if (wishlistArray.length === 0) {
      return null;
    }
    
    const wishlistData = {
      products: wishlistArray,
      timestamp: Date.now(),
      version: '1.0'
    };
    
    const encodedData = btoa(JSON.stringify(wishlistData));
    const currentUrl = new URL(window.location.origin + '/pages/wishlist');
    currentUrl.searchParams.set('shared', encodedData);
    
    return currentUrl.toString();
  }

  // New method to import shared wishlist
  importSharedWishlist(encodedData) {
    try {
      const decodedData = JSON.parse(atob(encodedData));
      
      if (!decodedData.products || !Array.isArray(decodedData.products)) {
        throw new Error('Invalid wishlist data');
      }
      
      const currentWishlist = [...this.wishlist];
      const sharedProducts = decodedData.products;
      const newProducts = sharedProducts.filter(handle => !this.wishlist.has(handle));
      const productsOnlyInCurrent = currentWishlist.filter(handle => !sharedProducts.includes(handle));
      
      if (newProducts.length === 0 && productsOnlyInCurrent.length === 0) {
        this.showShareNotification('Your wishlists are identical!', 'info');
        return;
      }
      
      // Show import dialog with detailed comparison
      this.showImportDialog(currentWishlist, sharedProducts, newProducts, productsOnlyInCurrent);
      
    } catch (error) {
      console.error('Error importing shared wishlist:', error);
      this.showShareNotification('Invalid or corrupted share link', 'error');
    }
  }

  // Updated method to show import dialog with better comparison
  showImportDialog(currentWishlist, sharedProducts, newProducts, productsOnlyInCurrent) {
    const dialog = document.createElement('div');
    dialog.className = 'wishlist-import-dialog';
    
    let dialogContent = `
      <div class="wishlist-import-dialog__backdrop">
        <div class="wishlist-import-dialog__content">
          <h3>Import Shared Wishlist</h3>
          <div class="wishlist-comparison">
            <div class="comparison-section">
              <h4>📤 Shared Wishlist</h4>
              <p>${sharedProducts.length} product${sharedProducts.length !== 1 ? 's' : ''}</p>
            </div>
            <div class="comparison-section">
              <h4>📝 Your Current Wishlist</h4>
              <p>${currentWishlist.length} product${currentWishlist.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
    `;

    if (newProducts.length === 0 && productsOnlyInCurrent.length > 0) {
      dialogContent += `
        <div class="comparison-details">
          <p class="info-message">✅ All shared products are already in your wishlist!</p>
          <p class="warning-message">⚠️ You have <strong>${productsOnlyInCurrent.length} extra product${productsOnlyInCurrent.length !== 1 ? 's' : ''}</strong> that ${productsOnlyInCurrent.length === 1 ? 'is' : 'are'} not in the shared wishlist.</p>
          <p>Would you like to replace your wishlist with only the shared products?</p>
        </div>
        <div class="wishlist-import-dialog__actions">
          <button class="button button--secondary" id="cancel-import">Keep My Wishlist</button>
          <button class="button button--primary" id="replace-wishlist">Replace with Shared</button>
        </div>
      `;
    } else if (newProducts.length > 0 && productsOnlyInCurrent.length === 0) {
      dialogContent += `
        <div class="comparison-details">
          <p class="success-message">✨ <strong>${newProducts.length} new product${newProducts.length !== 1 ? 's' : ''}</strong> will be added to your wishlist.</p>
        </div>
        <div class="wishlist-import-dialog__actions">
          <button class="button button--secondary" id="cancel-import">Cancel</button>
          <button class="button button--primary" id="merge-wishlist">Add New Products</button>
        </div>
      `;
    } else if (newProducts.length > 0 && productsOnlyInCurrent.length > 0) {
      dialogContent += `
        <div class="comparison-details">
          <p class="success-message">✨ <strong>${newProducts.length} new product${newProducts.length !== 1 ? 's' : ''}</strong> will be added.</p>
          <p class="warning-message">⚠️ You have <strong>${productsOnlyInCurrent.length} product${productsOnlyInCurrent.length !== 1 ? 's' : ''}</strong> that ${productsOnlyInCurrent.length === 1 ? 'is' : 'are'} not in the shared wishlist.</p>
        </div>
        <div class="wishlist-import-dialog__actions">
          <button class="button button--secondary" id="cancel-import">Cancel</button>
          <button class="button button--primary" id="merge-wishlist">Add New Products</button>
          <button class="button button--primary" id="replace-wishlist">Replace My Wishlist</button>
        </div>
      `;
    }

    dialogContent += `
        </div>
      </div>
    `;

    dialog.innerHTML = dialogContent;
    document.body.appendChild(dialog);
    
    // Handle dialog actions
    dialog.querySelector('#cancel-import')?.addEventListener('click', () => {
      dialog.remove();
      this.cleanUrl();
    });
    
    dialog.querySelector('#merge-wishlist')?.addEventListener('click', () => {
      // Add only new products
      newProducts.forEach(handle => this.wishlist.add(handle));
      this.saveWishlist();
      this.updateWishlistCount();
      this.showShareNotification(`Added ${newProducts.length} new product${newProducts.length !== 1 ? 's' : ''} to your wishlist!`, 'success');
      dialog.remove();
      this.cleanUrl();
      // Refresh the wishlist page if we're on it
      if (window.location.pathname.includes('/pages/wishlist')) {
        window.location.reload();
      }
    });
    
    dialog.querySelector('#replace-wishlist')?.addEventListener('click', () => {
      // Replace entire wishlist
      this.wishlist.clear();
      sharedProducts.forEach(handle => this.wishlist.add(handle));
      this.saveWishlist();
      this.updateWishlistCount();
      
      const removedCount = productsOnlyInCurrent.length;
      const addedCount = newProducts.length;
      let message = `Replaced your wishlist with ${sharedProducts.length} shared product${sharedProducts.length !== 1 ? 's' : ''}!`;
      
      if (removedCount > 0 && addedCount > 0) {
        message = `Replaced wishlist: removed ${removedCount}, added ${addedCount} product${addedCount !== 1 ? 's' : ''}!`;
      } else if (removedCount > 0) {
        message = `Replaced wishlist: removed ${removedCount} product${removedCount !== 1 ? 's' : ''}!`;
      }
      
      this.showShareNotification(message, 'success');
      dialog.remove();
      this.cleanUrl();
      // Refresh the wishlist page if we're on it
      if (window.location.pathname.includes('/pages/wishlist')) {
        window.location.reload();
      }
    });
  }

  // New method to show share notifications
  showShareNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `wishlist-share-notification wishlist-share-notification--${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 4000);
  }

  // New method to clean URL parameters
  cleanUrl() {
    const url = new URL(window.location);
    url.searchParams.delete('shared');
    window.history.replaceState({}, '', url.toString());
  }

  // New method to check for shared wishlist on page load
  checkForSharedWishlist() {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedData = urlParams.get('shared');
    
    if (sharedData) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.importSharedWishlist(sharedData);
      }, 500);
    }
  }
}

// WishlistPageHandler class for handling the wishlist page functionality
class WishlistPageHandler {
  constructor() {
    if (window.location.pathname.includes('/pages/wishlist')) {
      this.container = document.querySelector('[data-wishlist-container]');
      this.emptyMessage = document.querySelector('.empty-wishlist');
      this.wishlistCount = document.querySelector('.wishlist-count');
      this.wishlistCountNumber = this.wishlistCount?.querySelector('[aria-hidden="true"]');
      if (this.container) {
        this.init();
      }
    }
  }

  init() {
    this.renderWishlist();
    this.setupEventListeners();
    this.setupShareButton();
  }
  
  getWishlist() {
    const wishlist = localStorage.getItem('wishlist');
    return wishlist ? JSON.parse(wishlist) : [];
  }

  async renderWishlist() {
    const wishlist = this.getWishlist();
    
    if (wishlist.length === 0) {
      this.container.style.display = 'none';
      this.emptyMessage.style.display = 'block';
      this.hideShareButton();
      return;
    }

    this.container.style.display = 'grid';
    this.emptyMessage.style.display = 'none';
    this.showShareButton();
    
    try {
      const products = await Promise.all(
        wishlist.map(async (productHandle) => {
          try {
            const response = await fetch(`/products/${productHandle}.js`);
            if (!response.ok) throw new Error('Product not found');
            const product = await response.json();
            
            return {
              title: product.title,
              price: product.price,
              featured_image: product.featured_image,
              handle: product.handle,
              variant_id: product.variants[0].id
            };
          } catch (error) {
            console.error(`Error fetching product ${productHandle}:`, error);
            return null;
          }
        })
      );

      const validProducts = products.filter(p => p !== null);

      if (validProducts.length === 0) {
        this.container.style.display = 'none';
        this.emptyMessage.style.display = 'block';
        this.hideShareButton();
        return;
      }

      this.container.innerHTML = validProducts.map(product => `
  <div class="wishlist-item card-border" data-product-handle="${product.handle}">
    <div class="wishlist-item__image-wrapper">
      <img src="${product.featured_image}" alt="${product.title}" class="wishlist-item__image">
    </div>
    <div class="wishlist-item__content">
      <h3 class="wishlist-item__title h4">${product.title}</h3>
      <p class="wishlist-item__price text-size-medium">BHD ${(product.price/100).toFixed(3)}</p>
      <div class="wishlist-item__buttons">
        <a href="/products/${product.handle}" class="button button--primary">View Product</a>
        <button class="button button--secondary" id="remove-from-wishlist" data-product-handle="${product.handle}">
          <span class="remove-text">Remove</span>
        </button>
      </div>
    </div>
  </div>
`).join('');

    } catch (error) {
      console.error('Error loading wishlist products:', error);
      this.container.innerHTML = '<p>Error loading wishlist products. Please try again later.</p>';
    }
  }

  setupEventListeners() {
    this.container.addEventListener('click', (e) => {
      if (e.target.id === 'remove-from-wishlist') {
        const productHandle = e.target.dataset.productHandle;
        this.removeFromWishlist(productHandle);
      }
    });
  }

  removeFromWishlist(productHandle) {
    // Use the WishlistSystem instance to remove the product
    const wishlistSystem = window.wishlistSystemInstance;
    if (wishlistSystem) {
      wishlistSystem.removeFromWishlist(productHandle);
    } else {
      // Fallback to direct localStorage manipulation if system instance is not available
      const wishlist = this.getWishlist();
      const updatedWishlist = wishlist.filter(handle => handle !== productHandle);
      localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
      
      // Update the count in the header
      if (this.wishlistCount && this.wishlistCountNumber) {
        const count = updatedWishlist.length;
        this.wishlistCountNumber.textContent = count;
        this.wishlistCount.classList.toggle('opacity-0', count === 0);
      }
    }

    this.renderWishlist();
  }

  // Setup share button event listener
  setupShareButton() {
    const shareButton = document.querySelector('#share-wishlist-btn');
    if (shareButton) {
      shareButton.addEventListener('click', () => this.shareWishlist());
    }
  }

  // New method to show/hide share button
  showShareButton() {
    const shareButton = document.querySelector('#share-wishlist-btn');
    if (shareButton) {
      shareButton.style.display = 'inline-flex';
    }
  }

  hideShareButton() {
    const shareButton = document.querySelector('#share-wishlist-btn');
    if (shareButton) {
      shareButton.style.display = 'none';
    }
  }

  // New method to handle wishlist sharing
  shareWishlist() {
    const wishlistSystem = window.wishlistSystemInstance;
    if (!wishlistSystem) return;

    const shareUrl = wishlistSystem.generateShareableUrl();
    if (!shareUrl) {
      wishlistSystem.showShareNotification('Your wishlist is empty!', 'error');
      return;
    }

    // Try to use native sharing first
    if (navigator.share) {
      navigator.share({
        title: 'Check out my wishlist!',
        text: 'I wanted to share my wishlist with you.',
        url: shareUrl
      }).catch(() => {
        // Fall back to copy to clipboard
        this.copyToClipboard(shareUrl);
      });
    } else {
      // Fall back to copy to clipboard
      this.copyToClipboard(shareUrl);
    }
  }

  // New method to copy URL to clipboard
  copyToClipboard(url) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        const wishlistSystem = window.wishlistSystemInstance;
        if (wishlistSystem) {
          wishlistSystem.showShareNotification('Wishlist link copied to clipboard!', 'success');
        }
      }).catch(() => {
        this.fallbackCopyToClipboard(url);
      });
    } else {
      this.fallbackCopyToClipboard(url);
    }
  }

  // Fallback copy method for older browsers
  fallbackCopyToClipboard(url) {
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      const wishlistSystem = window.wishlistSystemInstance;
      if (wishlistSystem) {
        wishlistSystem.showShareNotification('Wishlist link copied to clipboard!', 'success');
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      const wishlistSystem = window.wishlistSystemInstance;
      if (wishlistSystem) {
        wishlistSystem.showShareNotification('Failed to copy link. Please copy manually: ' + url, 'error');
      }
    }
    
    document.body.removeChild(textArea);
  }
}

// Styles for wishlist functionality
const styles = `
  /* Wishlist Page Styles */
[data-wishlist-container] {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
}

.empty-wishlist {
  text-align: center;
  padding: 4rem 2rem;
  background: #f9f9f9;
  border-radius: 8px;
  margin: 2rem 0;
}

.empty-wishlist h2 {
  color: #052D67;
  margin-bottom: 1rem;
}

.wishlist-item {
  position: relative;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.wishlist-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.wishlist-item__image-wrapper {
  position: relative;
  padding-top: 100%;
  overflow: hidden;
  background: #f5f5f5;
}

.wishlist-item__image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.wishlist-item:hover .wishlist-item__image {
  transform: scale(1.05);
}

.wishlist-item__content {
  padding: 1.5rem;
}

.wishlist-item__title {
  color: #052D67;
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.4;
}

.wishlist-item__price {
  color: #052D67;
  font-weight: 500;
  margin-bottom: 1rem;
}

.wishlist-item__buttons {
  display: flex;
  gap: 1rem;
  flex-direction: column;
}

/* Import Dialog Styles */
.wishlist-import-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
}

.wishlist-import-dialog__backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.wishlist-import-dialog__content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.wishlist-import-dialog__content h3 {
  margin: 0 0 1.5rem;
  color: #052D67;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
}

.wishlist-import-dialog__content h4 {
  margin: 0 0 0.5rem;
  color: #052D67;
  font-size: 1rem;
  font-weight: 600;
}

.wishlist-comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.comparison-section {
  text-align: center;
}

.comparison-section p {
  margin: 0;
  color: #666;
  font-size: 0.9rem;
}

.comparison-details {
  margin-bottom: 1.5rem;
}

.comparison-details p {
  margin: 0 0 0.75rem;
  line-height: 1.5;
  text-align: center;
}

.info-message {
  color: #10b981;
  font-weight: 500;
}

.success-message {
  color: #10b981;
  font-weight: 500;
}

.warning-message {
  color: #f59e0b;
  font-weight: 500;
}

.wishlist-import-dialog__actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.wishlist-import-dialog__actions .button {
  flex: 1;
  min-width: 120px;
  text-align: center;
  padding: 0.75rem 1rem;
}

/* Share Notification Styles */
.wishlist-share-notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 16px 24px;
  border-radius: 8px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  animation: slideIn 0.3s ease-out;
  max-width: 400px;
  font-weight: 500;
}

.wishlist-share-notification--success {
  background: #10b981;
  color: white;
}

.wishlist-share-notification--error {
  background: #ef4444;
  color: white;
}

.wishlist-share-notification--info {
  background: #3b82f6;
  color: white;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  [data-wishlist-container] {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
    padding: 1rem;
  }

  .wishlist-item__content {
    padding: 1rem;
  }

  .wishlist-import-dialog__backdrop {
    padding: 1rem;
  }

  .wishlist-import-dialog__actions {
    flex-direction: column;
  }

  .wishlist-comparison {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .wishlist-share-notification {
    bottom: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
}

/* Animation for removing items */
.wishlist-item.removing {
  animation: fadeOut 0.3s ease forwards;
}

@keyframes fadeOut {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.95); }
}

/* Notification styles */
.wishlist-notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #052D67;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
`;

// Add styles to document
const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

// Initialize both systems when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const wishlistSystem = new WishlistSystem();
  window.wishlistSystemInstance = wishlistSystem; // Make it globally accessible
  
  // Check for shared wishlist
  wishlistSystem.checkForSharedWishlist();
  
  if (window.location.pathname.includes('/pages/wishlist')) {
    new WishlistPageHandler();
  }
});